import type { UiLang, VehicleKind } from "../lib/types";

/**
 * Hvor man leier bil, og hvorfor det er et søk og ikke en lenke.
 *
 * Samme spørsmål som `packingLink.ts` og `videoLink.ts` svarer på, og samme
 * svar av samme grunn. Tre alternativer ble vurdert og forkastet:
 *
 *   **En bestemt kjede.** Hertz, Avis, Sixt, Europcar finnes alle i Norge, og
 *   å lenke til én av dem er en anbefaling appen ikke har grunnlag for å gi —
 *   den vet ingenting om pris, tilgjengelighet eller hvor bilen kan leveres.
 *
 *   **En sammenligningstjeneste.** De tar provisjon, og en app som sender
 *   brukeren dit uten å si det har en interesse den ikke har opplyst om.
 *
 *   **En dyplenke med sted og dato.** URL-formatet for et slikt søk er ikke noe
 *   denne appen kan vite er riktig uten å teste det mot den ekte nettsiden — og
 *   en lenke som *ser* ut til å søke og lander på en feilmelding er verre enn
 *   ingen lenke.
 *
 * Et søk med stedet i er ærlig: det sier hva man leter etter, det lover ingen
 * bestemt leverandør, og det fungerer på hvert eneste sted langs hver rute.
 */

/**
 * Søkeordet, per språk og per kjøretøy.
 *
 * Appen har visst om bobil hele tiden — `VehicleKind` har den, planen sier hvor
 * det står en lengdegrense og hvor bakken er bratt nok til at den merkes —
 * men utleielenka spurte etter «leiebil» uansett. En bobilferie i Norge
 * begynner ikke med en personbil, og et søk som svarer på noe annet enn det
 * man planlegger er verre enn ingen lenke.
 *
 * Motorsykkel deler ikke skjebne med de to: utleie av MC er smått i Norge, og
 * søket skal si hva man faktisk leter etter framfor å love et marked.
 */
const RENT_PHRASE: Record<VehicleKind, Record<UiLang, string>> = {
  bil: {
    no: "leiebil",
    en: "car rental",
    de: "Mietwagen",
    fr: "location de voiture",
    es: "alquiler de coches",
  },
  bobil: {
    no: "bobilutleie",
    en: "motorhome rental",
    de: "Wohnmobil mieten",
    fr: "location de camping-car",
    es: "alquiler de autocaravanas",
  },
  motorsykkel: {
    no: "motorsykkelutleie",
    en: "motorcycle rental",
    de: "Motorrad mieten",
    fr: "location de moto",
    es: "alquiler de motos",
  },
};

export function rentalSearchUrl(
  place: string,
  lang: UiLang,
  vehicle: VehicleKind = "bil",
): string {
  const query = `${RENT_PHRASE[vehicle][lang]} ${place}`;
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}
